import type { DataMode } from './dataMode'

/**
 * Which dev tool may run in which world.
 *
 * Shared by both sides on purpose — `src/` renders from it and `server/routes/dev.ts` enforces it
 * — the same pattern `applyAutoPredict` uses. Two booleans cannot do this job: `canUseDevTools`
 * on the client and `method !== 'GET'` on the server put "wipe every user's data" and "clear my
 * own reveal-seen flag" on identical footing, and classify any route added later as season
 * simulation by virtue of being a POST.
 *
 * The rule the table encodes: **a dev tool moves the pipeline's input, it does not fake the
 * pipeline's output.** In `mock` there is no pipeline, so the simulation tools stay the writer.
 * In `replica` the input is the replica's clock. In `live` there is no input we own, so only
 * observation and per-user-local tools survive.
 */
export type DevTool =
  | 'simulateDeadline'
  | 'revealResults'
  | 'finalizeGameweek'
  | 'simulateSeason'
  | 'playLive'
  | 'changeGameweek'
  | 'clearRevealSeen'
  /** The replica's clock, reachable only through our own server. */
  | 'replicaClock'
  /** Reads the golden final scores the bundled season is played toward, before they happen. */
  | 'replicaSpoilers'
  /** Read-only, and the one thing worth looking at on a live matchday. */
  | 'footballStatus'
  | 'resetAll'
  /** Re-fetches football data; the only tool here that spends provider requests. */
  | 'resyncFootballData'
  /** Gameplay + football data + the replica's clock and season dates, in one sequence. */
  | 'resetEverything'
  /** One press per state worth testing; composes the clock controls. */
  | 'scenarioPreset'
  // ── The backoffice ──────────────────────────────────────────────────────────────────────────
  // Deliberately members of *this* union rather than a second scheme: a backoffice with live-safe
  // POSTs beside mock-only POSTs cannot be classified by HTTP verb, and the map that can classify
  // them already exists.
  /** Every read-only admin panel. Read-only, our own tables, all three modes. */
  | 'adminRead'
  /** Correct a match by hand. Available in `live`: that is the mode it exists for. */
  | 'fixtureOverride'
  /** Pause/resume the scheduler, clear a resource's failure backoff. */
  | 'schedulerControl'
  /** Re-run scoring for a gameweek from the results we already hold. */
  | 'rescoreGameweek'
  /** Rename an offensive display name or league motto. */
  | 'adminRename'
  /** Delete a user and every row they own. Its own member, never folded into `adminRename`:
   *  the two are not the same act, and a capability that covers both cannot be granted for one. */
  | 'adminDeleteUser'
  /** Purge all 10 seeded mock-friend bots and everything they own, in one act. Its own
   *  member rather than folded into `adminDeleteUser`: the single-account route refuses a bot
   *  target outright, so the two routes are never granted by the same capability. */
  | 'adminPurgeMockFriends'

/** Every member of the union, listed once, so a runtime exhaustiveness check is possible. */
export const DEV_TOOLS: readonly DevTool[] = [
  'simulateDeadline',
  'revealResults',
  'finalizeGameweek',
  'simulateSeason',
  'playLive',
  'changeGameweek',
  'clearRevealSeen',
  'replicaClock',
  'replicaSpoilers',
  'footballStatus',
  'resetAll',
  'resyncFootballData',
  'resetEverything',
  'scenarioPreset',
  'adminRead',
  'fixtureOverride',
  'schedulerControl',
  'rescoreGameweek',
  'adminRename',
  'adminDeleteUser',
  'adminPurgeMockFriends',
]

/**
 * Modes a tool may run in.
 *
 * `Record<DevTool, …>` is the type-level half of "no tool ships unclassified": a new member of the
 * union fails to compile until it has a row. `isDevToolAvailable` is the runtime half — a name
 * with no row is available nowhere rather than everywhere.
 */
export const DEV_TOOL_MODES: Record<DevTool, readonly DataMode[]> = {
  // Writes `gameweek_phase` directly. Against a replica the scheduler's derive pass
  // overwrites it within a tick, so the two would fight and the last writer would win.
  simulateDeadline: ['mock'],
  revealResults: ['mock'],
  // Plus: scoring is continuous, so a one-shot finalize is the wrong shape entirely.
  finalizeGameweek: ['mock'],
  // The dangerous one: 38 gameweeks of fabricated results for every user, no undo.
  simulateSeason: ['mock'],
  // Same button, a different implementation per mode.
  playLive: ['mock', 'replica'],
  // The stepper writes the per-user `current_gameweek` row, a replica of one global pointer. In
  // replica the tool that moves the season is the clock: the derive pass runs on that clock, so
  // moving the pointer by hand against a clock that says otherwise is a two-writers problem. The
  // honest way to move the season there is `at-deadline` → `full-time` → `settled`.
  changeGameweek: ['mock'],
  // Per-user, local, cosmetic, and undone by a reload.
  clearRevealSeen: ['mock', 'replica', 'live'],
  // Nothing to talk to in mock; a clock rewrite against real data in live.
  replicaClock: ['replica'],
  // Reads the bundled season's known final scores so the picker can show the score a fixture is
  // already going to have. Mock and replica play toward the same golden file, so both qualify.
  // Live is out because there is nothing to read, not because it would be dangerous: real football
  // has no answer sheet.
  replicaSpoilers: ['mock', 'replica'],
  // Read-only. Deliberately available in live — that is the mode it was built for.
  footballStatus: ['mock', 'replica', 'live'],
  // Destructive everywhere, and the only way out of a bad state, so it survives behind a typed
  // confirmation in live rather than being refused.
  resetAll: ['mock', 'replica', 'live'],
  // Re-fetches, never fabricates: the recovery path when a partial sync left bad fixtures.
  resyncFootballData: ['mock', 'replica', 'live'],
  // The union of the two above plus the replica's clock. Destructive everywhere,
  // and available everywhere for exactly the reason `resetAll` is — a state you cannot get out of
  // is worse than a button that can be pressed by mistake — so live gets the friction instead: a
  // typed `RESET EVERYTHING`, which is deliberately harder to type than either half.
  resetEverything: ['mock', 'replica', 'live'],
  // Composes the clock controls, so it inherits their mode: nothing to move in mock, and a clock
  // rewrite against real data in live.
  scenarioPreset: ['replica'],

  // Read-only, reads our own tables, spends nothing. Available everywhere for the same reason
  // `footballStatus` is: the mode you most need to observe is the one you cannot simulate.
  adminRead: ['mock', 'replica', 'live'],
  // The one mutating backoffice tool that is *most* needed in live: if the provider
  // publishes 2-1 for a 2-2, every user is scored wrong until somebody can say otherwise. It is
  // an override applied at read time, never a write to the provider's row, so a sync undoes
  // nothing and clearing it hands control straight back.
  fixtureOverride: ['mock', 'replica', 'live'],
  // Stopping the scheduler writing to our database while you work out what the provider is doing
  // is an operator capability, not a simulation. Nothing it does fabricates a result.
  schedulerControl: ['mock', 'replica', 'live'],
  // Re-runs scoring over the results we already hold. Unlike `finalizeGameweek` it invents no
  // phase and advances nothing — which is why it survives in live, where correcting a match by
  // hand is the whole reason it is needed.
  rescoreGameweek: ['mock', 'replica', 'live'],
  // An offensive display name sits on every leaderboard until someone can change it, and that is
  // as true in live as anywhere.
  adminRename: ['mock', 'replica', 'live'],
  // Live is the mode it exists for: an erasure request is about a real account, and refusing it
  // there would leave the only answer as SQL by hand. The friction goes in the
  // confirmation — a typed display name, keyed to the target — not in a mode ban, exactly as
  // `resetAll` argues.
  adminDeleteUser: ['mock', 'replica', 'live'],
  // Live is the mode this exists for: getting a real deployment to real-accounts-only
  // play. Available everywhere for the same reason `adminDeleteUser` is — the friction is the typed
  // confirmation in the route, not a mode ban.
  adminPurgeMockFriends: ['mock', 'replica', 'live'],
}

/**
 * Whether a tool may run in a mode.
 *
 * Fails safe twice over: an unknown tool and an unknown mode both answer `false`. A mode of `null`
 * is the window between boot and `/me` landing, where the honest answer is "we don't know yet".
 */
export function isDevToolAvailable(tool: DevTool, mode: DataMode | null | undefined): boolean {
  if (!mode) return false
  if (!Object.hasOwn(DEV_TOOL_MODES, tool)) return false
  return DEV_TOOL_MODES[tool].includes(mode)
}

/** The tools available in a mode, in declaration order. Empty for an unknown mode. */
export function availableDevTools(mode: DataMode | null | undefined): DevTool[] {
  return DEV_TOOLS.filter((tool) => isDevToolAvailable(tool, mode))
}

/** Whether the rail has anything at all to show — the client-side "is there a rail" question. */
export function hasAnyDevTool(mode: DataMode | null | undefined): boolean {
  return availableDevTools(mode).length > 0
}
